import { BIKE_BONUS_PREMIUM } from '@vivaldis/web-ui'
import {
  FormikContextType,
  FormikProps,
  useFormikContext,
  Formik,
  FormikHelpers
} from 'formik'
import { useMemo, useCallback, PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import {
  LATE_SHIFT_BONUS_PREMIUM,
  MEAL_VOUCHERS_CONTRIBUTION_VALUE_MAX,
  MEAL_VOUCHERS_CONTRIBUTION_VALUE_MIN,
  MEAL_VOUCHERS_PREMIUM,
  MEAL_VOUCHERS_TOTAL_VALUE_MAX,
  MEAL_VOUCHERS_TOTAL_VALUE_MIN,
  MORNING_SHIFT_BONUS_PREMIUM,
  NIGHT_SHIFT_BONUS_PREMIUM,
  SOCIAL_KILOMETER_COMPENSATION_PREMIUM
} from '../../../../Premiums'
import { Premium } from '../../../typings/Premium'
import { PositionFormStep3Values } from '../typings/PositionFormStep3Values'

export function useForm(): FormikContextType<PositionFormStep3Values> {
  return useFormikContext()
}

export interface PositionFormProviderProps {
  onSubmit: (
    values: PositionFormStep3Values,
    formikHelpers: FormikHelpers<PositionFormStep3Values>
  ) => Promise<any>
  initialValues?: Partial<FormikProps<PositionFormStep3Values>['initialValues']>
  premiums: Premium[]
  showEcoChequeFields: boolean
}

export const FormProvider = ({
  onSubmit,
  initialValues,
  premiums,
  showEcoChequeFields,
  children
}: PropsWithChildren<PositionFormProviderProps>) => {
  const [t] = useTranslation()

  const formInitialValues: PositionFormStep3Values = useMemo(
    () => ({
      useNettoPremiumComment: false,
      nettoPremiumComment: null,
      premiumIds: [],
      premiumValuesInput: [],
      //
      useEcoChequeComment: null,
      ecoChequeComment: null,
      //
      ...initialValues
    }),
    [initialValues]
  )

  const ValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        premiumIds: Yup.array()
          .label(
            t('position_form.step3.premium_ids.label', {
              ns: 'web_ui'
            })
          )
          .required(),
        premiumValuesInput: Yup.array().of(
          Yup.object().shape({
            premiumId: Yup.string().label(t('form.this')).required(),
            value: Yup.number()
              .label(t('form.this'))
              .nullable()
              .notRequired()
              .when('premiumId', ([premiumId], schema) => {
                const premiumName = premiums.find(
                  premium => premium.id === premiumId
                )?.name

                switch (premiumName) {
                  case LATE_SHIFT_BONUS_PREMIUM:
                    return schema.min(0).required()
                  case NIGHT_SHIFT_BONUS_PREMIUM:
                    return schema.min(0).required()
                  case MORNING_SHIFT_BONUS_PREMIUM:
                    return schema.min(0).required()
                  case MEAL_VOUCHERS_PREMIUM:
                    return schema
                      .min(MEAL_VOUCHERS_TOTAL_VALUE_MIN)
                      .max(MEAL_VOUCHERS_TOTAL_VALUE_MAX)
                      .required()
                  case BIKE_BONUS_PREMIUM:
                    // NOTE: on position level this field should be empty (it's only required on contract level)
                    return schema.notRequired()
                  case SOCIAL_KILOMETER_COMPENSATION_PREMIUM:
                    // NOTE: on position level this field should be empty (it's only required on contract level)
                    return schema.notRequired()
                  default:
                    return schema
                }
              }),
            contributionValue: Yup.number()
              .label(t('form.this'))
              .nullable()
              .notRequired()
              .when('premiumId', ([premiumId], schema) => {
                const premiumName = premiums.find(
                  premium => premium.id === premiumId
                )?.name

                switch (premiumName) {
                  case MEAL_VOUCHERS_PREMIUM:
                    return schema
                      .min(MEAL_VOUCHERS_CONTRIBUTION_VALUE_MIN)
                      .max(MEAL_VOUCHERS_CONTRIBUTION_VALUE_MAX)
                      .required()
                      .when('value', ([value], schema) => {
                        if (!value) {
                          return schema
                        }
                        return schema.max(
                          value,
                          t(
                            'position_form.step3.meal_vouchers.contribution_value.errors.must_be_less_than_or_equal_to_meal_vouchers_total_value',
                            {
                              ns: 'web_ui'
                            }
                          )
                        )
                      })
                  default:
                    return schema
                }
              }),
            unit: Yup.string().label(t('form.this')).required()
          })
        ),
        // nettoPremiumComment
        useNettoPremiumComment: Yup.boolean()
          .label(
            t('position_form.step3.netto_premium_comment.label', {
              ns: 'web_ui'
            })
          )
          .required(),
        nettoPremiumComment: Yup.string()
          .label(
            t('position_form.step3.netto_premium_comment.label', {
              ns: 'web_ui'
            })
          )
          .nullable()
          .max(255)
          .when('useNettoPremiumComment', {
            is: true,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          }),
        // ecoChequeComment fields
        useEcoChequeComment: Yup.boolean()
          .label(t('form.this'))
          .nullable()
          // this question is required, only when User should see this fields
          .when({
            is: () => showEcoChequeFields,
            then: schema => schema.required()
          }),
        ecoChequeComment: Yup.string()
          .label(t('form.this'))
          .nullable()
          .max(255)
          .when('useEcoChequeComment', {
            is: true,
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired()
          })
      }),
    [premiums, showEcoChequeFields, t]
  )

  const handleSubmit = useCallback(
    async (
      values: PositionFormStep3Values,
      formikHelpers: FormikHelpers<PositionFormStep3Values>
    ) => {
      await onSubmit(values, formikHelpers)
      formikHelpers.setSubmitting(false)
    },
    [onSubmit]
  )

  return (
    <Formik<PositionFormStep3Values>
      enableReinitialize={true}
      initialValues={formInitialValues}
      validateOnChange={false}
      validateOnBlur={true}
      validationSchema={ValidationSchema}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  )
}
